/**
 * Zdieľané slovenské label mappingy pre enumy z Prisma schémy.
 * Používať v admin UI, mobilnej appke aj weboch — nie duplicitné definície.
 */
import type {
  ContactType,
  DocumentType,
  ExportPortal,
  LawyerRequestStatus,
  LeadSource,
  LeadStatus,
  MaritalStatus,
  MonitorContactType,
  MonitorPortal,
  PropertyStatus,
  TaskPriority,
  TaskStatus,
} from '@repo/database'
import type { UserRole, UserStatus } from '../domain/user.types'
import type { ContactAgentSource } from '../domain/contact.types'
import type { PropertyDealType, PropertyEnergyClass } from '../domain/property.types'
import { ENERGY_CLASS_TO_UC_CERT } from '../uc/uc-export'
import {
  UC_CONDITIONS,
  UC_OWNERSHIPS,
  UC_EQUIP_TYPES,
  UC_POSITIONS,
  UC_TERRITORIES,
  UC_GROUND_TYPES,
  UC_HOUSE_TYPES,
  UC_READY_TO_BUILD,
  UC_HEATING_SYSTEMS,
  UC_ORIENTATIONS,
  UC_BUILDING_TYPES,
  UC_PARKING_TYPES,
  UC_WATER,
  UC_ELECTRIC,
  UC_SEWAGE,
  UC_GAS,
  ucLabels,
  ucOptions,
} from '../uc/attributes'
import { PROPERTY_SUBTYPE_DEFS } from '../uc/categories'

// Typ dokumentu obchodu (DealDocument.type = Prisma DocumentType) — výber pri
// nahrávaní, odznak v zozname dokumentov aj v právnych požiadavkách.
// Poradie = poradie v selecte.
export const CONTRACT_TYPE_LABELS: Record<string, string> = {
  RESERVATION: 'Rezervačná zmluva',
  EXCLUSIVE: 'Výhradná zmluva',
  PURCHASE: 'Kúpna zmluva',
  FUTURE_PURCHASE: 'Zmluva o budúcej kúpnej zmluve',
  WORK: 'Zmluva o dielo',
  MOVABLE_PROPERTY: 'Zmluva o hnuteľnom majetku',
  ESCROW: 'Zmluva o úschove',
  GIFT: 'Darovacia zmluva',
  SETTLEMENT: 'Dohoda o urovnaní',
  RENTAL: 'Zmluva o prenájme',
  NEEDS_ADVICE: 'Potrebujem poradiť',
  AMENDMENT: 'Dodatok',
  CERTIFICATE: 'Certifikát',
  OTHER: 'Iné',
} satisfies Record<DocumentType, string>

export const CONTRACT_TYPE_OPTIONS: Array<{ value: string; label: string }> = Object.entries(
  CONTRACT_TYPE_LABELS,
).map(([value, label]) => ({ value, label }))

// Rola používateľa — interné znenie (admin, mobil). Verejné weby majú vlastné
// AGENT_ROLE_LABELS v @repo/ui („Realitný maklér").
export const USER_ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin',
  OWNER: 'Majiteľ',
  BRANCH_MANAGER: 'Vedúci pobočky',
  BROKER: 'Maklér',
  ASSISTANT: 'Asistent',
  TIPER: 'Tiper',
  LAWYER: 'Právnik',
} satisfies Record<UserRole, string>

export const USER_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Aktívny',
  INACTIVE: 'Neaktívny',
  INVITED: 'Pozvaný',
  SUSPENDED: 'Pozastavený',
} satisfies Record<UserStatus, string>

export const LAWYER_REQUEST_STATUS_LABELS: Record<string, string> = {
  NEW: 'Nová',
  REVIEWED: 'Skontrolované',
  NEEDS_REVISION: 'Potrebná úprava',
} satisfies Record<LawyerRequestStatus, string>

export const LEAD_STATUS_LABELS: Record<string, string> = {
  NEW: 'Nový',
  CONTACTED: 'Kontaktovaný',
} satisfies Record<LeadStatus, string>

// Typ kontaktu (Contact.contactType) — kto osoba je, zadáva sa len pri vytvorení.
// Záujmy (a customerType) má len CLIENT („Záujemca"); staršie appky typ neposielajú → CLIENT.
export const CONTACT_TYPES = [
  'CLIENT', 'APPRAISER', 'FINANCIAL_INTERMEDIARY', 'NOTARY', 'LAWYER',
  'REAL_ESTATE_AGENCY', 'PROPERTY_MANAGER', 'OTHER',
] as const satisfies readonly ContactType[]
export type ContactTypeValue = (typeof CONTACT_TYPES)[number]

export const CONTACT_TYPE_LABELS: Record<string, string> = {
  CLIENT: 'Záujemca',
  APPRAISER: 'Znalec',
  FINANCIAL_INTERMEDIARY: 'Finančný sprostredkovateľ',
  NOTARY: 'Notár',
  LAWYER: 'Právnik',
  REAL_ESTATE_AGENCY: 'Realitná kancelária',
  PROPERTY_MANAGER: 'Správca / Domovník',
  OTHER: 'Iné',
} satisfies Record<ContactType, string>

export const CONTACT_TYPE_OPTIONS: Array<{ value: ContactTypeValue; label: string }> = CONTACT_TYPES.map(
  (value) => ({ value, label: CONTACT_TYPE_LABELS[value] ?? value }),
)

/** Kontakt so záujmami — typ Záujemca; chýbajúci typ (odpoveď staršieho API) je tiež Záujemca. */
export function isClientContactType(type: string | null | undefined): boolean {
  return !type || type === 'CLIENT'
}

/** Prečo je používateľ agentom osoby (contact_agents.source) — popis pri agentovi v detaile osoby. */
export const CONTACT_AGENT_SOURCE_LABELS: Record<ContactAgentSource, string> = {
  CREATED: 'Založil kontakt',
  ASSIGNED: 'Pridaný ako agent',
  CONTACTED: 'Kontaktoval',
  CALL: 'Hovor s kontaktom',
  SAVED: 'Uložil si číslo',
  INTEREST: 'Autor záujmu',
  CLAIMED: 'Pracuje s kontaktom',
  CONVERTED: 'Z prevodu kontaktu',
  TAKEN: 'Prevzal z poolu',
  MIGRATED: 'Z migrácie dát',
}

// Realitné portály — ExportPortal (história exportov), MonitorPortal (Reality
// Monitor) a Property.publishedPortals (+ BROKER_WEB). Zdroj kontaktu z portálu
// (LEAD_SOURCE_LABELS.PORTAL_*) berie názov odtiaľto.
const PORTAL_NAMES = {
  BROKER_WEB: 'Web makléra',
  NEHNUTELNOSTI_SK: 'Nehnuteľnosti.sk',
  REALITY_SK: 'Reality.sk',
  TOPREALITY_SK: 'TopReality.sk',
  BAZOS_SK: 'Bazos.sk',
  CUSTOM: 'Portfólio export',
} satisfies Record<ExportPortal | MonitorPortal | 'BROKER_WEB', string>
export const PORTAL_LABELS: Record<string, string> = PORTAL_NAMES

// Index = Date#getMonth(). Malým písmenom cez .toLowerCase() („21. október").
export const MONTH_LABELS = [
  'Január', 'Február', 'Marec', 'Apríl', 'Máj', 'Jún',
  'Júl', 'August', 'September', 'Október', 'November', 'December',
] as const

// Skratky dní od pondelka (hlavička kalendára). Z Date#getDay() (0 = nedeľa)
// cez index (getDay() + 6) % 7.
export const WEEKDAY_SHORT_LABELS = ['Po', 'Ut', 'St', 'Št', 'Pi', 'So', 'Ne'] as const

export const VIEWING_STATUS_LABELS: Record<string, string> = {
  SCHEDULED: 'Naplánovaná',
  CONFIRMED: 'Potvrdená',
  DONE: 'Prebehla',
  CANCELLED: 'Zrušená',
}

export const TASK_STATUS_LABELS: Record<string, string> = {
  TODO: 'Na vykonanie',
  IN_PROGRESS: 'Prebieha',
  DONE: 'Hotovo',
  CANCELLED: 'Zrušená',
} satisfies Record<TaskStatus, string>

export const TASK_PRIORITY_LABELS: Record<string, string> = {
  LOW: 'Nízka',
  MEDIUM: 'Stredná',
  HIGH: 'Vysoká',
  URGENT: 'Urgentná',
} satisfies Record<TaskPriority, string>

// `satisfies Record<LeadSource, string>` ties this to the Prisma LeadSource
// enum: adding an enum value without a label here is a compile error, so this
// stays the single source of truth. The `Record<string, string>` annotation
// keeps lookups permissive for callers that index with a plain string.
export const LEAD_SOURCE_LABELS: Record<string, string> = {
  WEB_FORM: 'Kontaktný formulár',
  BROKER_WEB: 'Maklérsky web',
  PORTAL_NEHNUTELNOSTI: PORTAL_NAMES.NEHNUTELNOSTI_SK,
  PORTAL_REALITY: PORTAL_NAMES.REALITY_SK,
  PORTAL_TOPREALITY: PORTAL_NAMES.TOPREALITY_SK,
  PORTAL_BAZOS: PORTAL_NAMES.BAZOS_SK,
  PHONE: 'Telefón',
  EMAIL: 'Email',
  REFERRAL: 'Tiper',
  CAMPAIGN: 'Kampaň',
  WALK_IN: 'Osobne',
  SOCIAL_MEDIA: 'Sociálne siete',
  MANUAL: 'Manuálny',
  OTHER: 'Iné',
} satisfies Record<LeadSource, string>

// Zdroj osoby (Contact.source je tiež LeadSource) — ako LEAD_SOURCE_LABELS,
// len WEB_FORM skrátene. Používa zásuvka osoby v kalendári admina.
export const CLIENT_SOURCE_LABELS: Record<string, string> = {
  ...LEAD_SOURCE_LABELS,
  WEB_FORM: 'Web',
}

// Hodnoty enumu TransactionKind (Contact.customerType, InterestEntry.interest).
// Dva pohľady na tú istú enumovú hodnotu:
//   - TRANSACTION_KIND_LABELS — akcia/transakcia ("Kúpa", "Predaj") — pre InterestEntry, form labels typu "Záujem o", filter „Typ transakcie" kontaktov
//   - CUSTOMER_TYPE_LABELS    — osoba/rola      ("Kupujúci", "Predávajúci") — pre badges osoby (zoznam, detail, ContactPicker, kalendár)
export const TRANSACTION_KINDS = ['BUYING', 'SELLING', 'RENTING', 'LETTING'] as const
export type TransactionKindValue = (typeof TRANSACTION_KINDS)[number]

export const TRANSACTION_KIND_LABELS: Record<string, string> = {
  BUYING: 'Kúpa',
  SELLING: 'Predaj',
  RENTING: 'Nájom',
  LETTING: 'Prenájom',
}

export const TRANSACTION_KIND_OPTIONS: Array<{ value: string; label: string }> = TRANSACTION_KINDS.map(
  (value) => ({ value, label: TRANSACTION_KIND_LABELS[value] ?? value }),
)

// Krátke labely izbových bytov pre caller ID („Kúpa Byty 2-izb.“) — štítok v telefóne má obmedzenú dĺžku.
const APARTMENT_SUBTYPE_SHORT_LABELS: Record<string, string> = {
  ONE_ROOM_APARTMENT: '1-izb.',
  TWO_ROOM_APARTMENT: '2-izb.',
  THREE_ROOM_APARTMENT: '3-izb.',
  FOUR_ROOM_APARTMENT: '4-izb.',
  FIVE_PLUS_ROOM_APARTMENT: '5+ izb.',
}

// Číselné inputy s oddelenými tisíckami (rozpočet v záujme klienta a pod.).
// Stav/URL/payload drží surové číslice (stripThousands), input zobrazuje
// formatThousands — parsing cez Number() tak zostáva bez zmeny.
export function stripThousands(value: string): string {
  return value.replace(/\D/g, '')
}

/** "1234567" → "1 234 567"; ne-číslice ignoruje, prázdny vstup → ''. */
export function formatThousands(value: string | number): string {
  const digits = stripThousands(String(value))
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}

// Kompaktný zápis budgetu: 250000 → „250k", 132500 → „132,5k"; hodnoty pod
// 1000 (nájomné budgety) sa nechávajú celé („800").
function formatPriceShortK(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return ''
  if (value < 1000) return String(Math.round(value))
  const thousands = Math.round((value / 1000) * 10) / 10
  return `${String(thousands).replace('.', ',')}k`
}

/**
 * Krátky súhrn záujmu pre caller ID a kompaktné zobrazenia:
 * „Kúpa Byty 2-izb. do 250k“. Berie prvý propertyType a prvý bytový druh
 * (pri dome či pozemku by druh len opakoval typ) — caller ID label má
 * obmedzenú dĺžku, viac hodnôt by sa aj tak odrezalo. priceTo akceptuje aj Prisma Decimal.
 */
export function formatInterestShort(entry: {
  interest: string
  propertyTypes?: string[] | null
  propertySubtypes?: string[] | null
  priceTo?: number | string | { toString(): string } | null
}): string {
  const parts = [TRANSACTION_KIND_LABELS[entry.interest] ?? entry.interest]
  const propertyType = entry.propertyTypes?.[0]
  if (propertyType) parts.push(PROPERTY_TYPE_LABELS[propertyType] ?? propertyType)
  const apartmentSubtype = entry.propertySubtypes?.find(
    (s) => PROPERTY_SUBTYPE_DEFS[s as keyof typeof PROPERTY_SUBTYPE_DEFS]?.group === 'APARTMENTS',
  )
  if (apartmentSubtype && (!propertyType || propertyType === 'APARTMENTS')) {
    parts.push(APARTMENT_SUBTYPE_SHORT_LABELS[apartmentSubtype] ?? PROPERTY_SUBTYPE_LABELS[apartmentSubtype] ?? apartmentSubtype)
  }
  if (entry.priceTo != null) {
    const price = formatPriceShortK(Number(String(entry.priceTo)))
    if (price) parts.push(`do ${price}`)
  }
  return parts.join(' ')
}

export const CUSTOMER_TYPE_LABELS: Record<string, string> = {
  BUYING: 'Kupujúci',
  SELLING: 'Predávajúci',
  RENTING: 'Nájomník',
  LETTING: 'Prenajímateľ',
}

export const CUSTOMER_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'BUYING', label: 'Kupujúci' },
  { value: 'SELLING', label: 'Predávajúci' },
  { value: 'RENTING', label: 'Nájomník' },
  { value: 'LETTING', label: 'Prenajímateľ' },
]

// Plurálne labels (pre filter chip-y, segmenty atď.).
export const CUSTOMER_TYPE_LABELS_PLURAL: Record<string, string> = {
  BUYING: 'Kupujúci',
  SELLING: 'Predávajúci',
  RENTING: 'Nájomníci',
  LETTING: 'Prenajímatelia',
}

// Tailwind badge classes per TransactionKind. Single source of truth for color coding.
export const CUSTOMER_TYPE_BADGE_CLASS: Record<string, string> = {
  BUYING: 'badge-blue',
  SELLING: 'badge-green',
  RENTING: 'badge-yellow',
  LETTING: 'badge-green',
}

// Suffix ceny podľa Property.priceType (admin: PropertyPriceSection "Typ ceny").
// Typy bez suffixu (TOTAL, ON_REQUEST, BY_AGREEMENT) tu zámerne nie sú.
export const PRICE_TYPE_SUFFIX: Record<string, string> = {
  PER_M2: '/m²',
  PER_MONTH: '/mesiac',
  PER_YEAR: '/rok',
  PER_M2_MONTH: '/m²/mesiac',
  PER_M2_YEAR: '/m²/rok',
  PER_DAY: '/deň',
}

// Jednotné zobrazenie ceny inzerátu na verejných weboch (web, tenant-web,
// broker-web) — jediný zdroj pravdy, aby web ukazoval presne ten typ ceny,
// ktorý maklér zadal. Prázdna/nulová cena = „Cena dohodou" (default, keď sa
// pri nahadzovaní cena nevyplní); „Cena na vyžiadanie" LEN pri skrytej cene
// (priceHidden) alebo explicitnom priceType ON_REQUEST.
export function formatPropertyPrice(
  price: number | null | undefined,
  currency = 'EUR',
  hidden = false,
  priceType?: string | null,
): string {
  if (hidden || priceType === 'ON_REQUEST') return 'Cena na vyžiadanie'
  if (priceType === 'BY_AGREEMENT' || !price) return 'Cena dohodou'
  const formatted = new Intl.NumberFormat('sk-SK', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(price)
  const suffix = priceType ? PRICE_TYPE_SUFFIX[priceType] : undefined
  return suffix ? `${formatted} ${suffix}` : formatted
}

// Inzerent v Reality Monitore (monitored_listings.contact_type) — detail, filter.
export const MONITOR_CONTACT_TYPE_LABELS: Record<string, string> = {
  PRIVATE: 'Súkromná osoba',
  AGENCY: 'Realitná kancelária',
} satisfies Record<MonitorContactType, string>

// Reality monitor: monitored_listings.transaction_type je PropertyTransactionType
// (SALE/RENT/…), NIE TransactionKind (BUYING/SELLING/…) — filter monitoru musí
// posielať tieto hodnoty, inak nikdy nič nenájde. Zdieľané admin + mobile.
export const MONITOR_TRANSACTION_LABELS: Record<string, string> = {
  SALE: 'Predaj',
  RENT: 'Prenájom',
  PURCHASE: 'Kúpa',
  AUCTION: 'Dražba',
  LEASE: 'Lízing',
}

// Hodnoty, ktoré scraper reálne klasifikuje (inferTransaction v apps/monitor
// pipeline/classify.ts). Filtre monitoru ponúkajú len tieto — voľba mimo
// tejto množiny by garantovane vrátila 0 výsledkov.
export const MONITOR_TRANSACTION_TYPES = ['SALE', 'RENT', 'PURCHASE', 'AUCTION'] as const
export const MONITOR_PROPERTY_TYPES = [
  'APARTMENTS', 'HOUSES', 'LANDS', 'SPACES', 'OBJECTS', 'GARAGES_AND_PARKING',
] as const

// Skupiny UC taxonómie. Konkrétny druh (63 podtypov) má vlastné labely
// v PROPERTY_SUBTYPE_DEFS — tieto sú len pre skupinu.
// GARAGES_AND_PARKING má tu zámerne kratší label ('Garáže a parkovanie') než
// jej UC podtyp v PROPERTY_SUBTYPE_DEFS ('Garáže a parkovacie miesta') — skupina
// je nadpis v skupinovom selecte/filtri, podtyp je plný UC názov v detaile.
export const PROPERTY_TYPE_LABELS: Record<string, string> = {
  APARTMENTS: 'Byty',
  HOUSES: 'Domy',
  LANDS: 'Pozemky',
  SPACES: 'Priestory',
  OBJECTS: 'Objekty',
  GARAGES_AND_PARKING: 'Garáže a parkovanie',
}

export const PROPERTY_TYPE_OPTIONS = Object.entries(PROPERTY_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}))

// Konkrétny druh nehnuteľnosti (63 UC podtypov) — label per podtyp, odvodené
// priamo z PROPERTY_SUBTYPE_DEFS (jediný zdroj pravdy, žiadny duplicitný zoznam).
// PROPERTY_SUBTYPE_DEFS má tvar UcTableDef (+ group/deprecated) — ucLabels sa dá
// použiť aj tu bez zvláštneho helpera.
export const PROPERTY_SUBTYPE_LABELS: Record<string, string> = ucLabels(PROPERTY_SUBTYPE_DEFS)

// Deprecated podtypy (UC ich už nepoužíva) sa v UI neponúkajú.
export const PROPERTY_SUBTYPE_OPTIONS: Array<{ value: string; label: string }> = Object.entries(
  PROPERTY_SUBTYPE_DEFS,
)
  .filter(([, def]) => !def.deprecated)
  .map(([value, def]) => ({ value, label: def.label }))

export const PROPERTY_STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Koncept',
  IN_REVIEW: 'Na schválenie',
  ACTIVE: 'Aktívna',
  RESERVED: 'Rezervovaná',
  SOLD: 'Predaná',
  RENTED: 'Prenajatá',
  INACTIVE: 'Neaktívna',
  ARCHIVED: 'Archivovaná',
} satisfies Record<PropertyStatus, string>

// Stavy na výber vo formulári inzerátu (admin aj mobil). IN_REVIEW sa ručne nevolí.
export const PROPERTY_STATUS_OPTIONS: Array<{ value: string; label: string }> = (
  ['DRAFT', 'ACTIVE', 'INACTIVE', 'RESERVED', 'SOLD', 'RENTED', 'ARCHIVED'] satisfies PropertyStatus[]
).map((value) => ({ value, label: PROPERTY_STATUS_LABELS[value] ?? value }))

// Property.dealType — typ ponuky (formulár, detail, filter; admin aj mobil).
export const PROPERTY_DEAL_TYPE_LABELS: Record<string, string> = {
  EXCLUSIVE: 'Výhradná zmluva',
  NON_EXCLUSIVE: 'Nevýhradná zmluva',
  OFF_MARKET: 'Podpultová ponuka',
}
export const PROPERTY_DEAL_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'EXCLUSIVE', label: 'Výhradná zmluva' },
  { value: 'NON_EXCLUSIVE', label: 'Nevýhradná zmluva' },
  { value: 'OFF_MARKET', label: 'Podpultová ponuka' },
]
// Krátky tvar na odznak karty (Pipeline v admine aj mobile); plný názov v title.
export const PROPERTY_DEAL_TYPE_SHORT_LABELS: Record<string, string> = {
  EXCLUSIVE: 'Výhradná',
  NON_EXCLUSIVE: 'Nevýhradná',
  OFF_MARKET: 'Podpultová',
} satisfies Record<PropertyDealType, string>
export const PROPERTY_OFF_MARKET_PUBLISH_HINT = 'Ponuka je podpultová, najprv zmeňte typ'

/**
 * Zverejnenie čaká na fotky (media worker): text 409 / chyby validácie z API aj pruhu
 * v admine a mobile — jeden zdroj, aby sa hlášky nerozišli.
 */
export const photosProcessingPublishHint = (count: number): string =>
  `Fotky sa ešte spracúvajú (${count}). Zverejniť bude možné po ich dokončení.`

/** Už zverejnená ponuka, ku ktorej sa ešte spracúvajú nové fotky — ostáva zverejnená. */
export const photosProcessingLiveHint = (count: number): string =>
  `Nové fotky sa spracúvajú (${count}). Na weby a portály pôjdu po dokončení.`

// Property.transactionType — plné labely pre admin (PropertyBasicsSection select,
// história zmien). MONITOR_TRANSACTION_LABELS vyššie sú pre monitor scraping.
export const PROPERTY_TRANSACTION_TYPE_LABELS: Record<string, string> = {
  SALE: 'Predaj',
  RENT: 'Prenájom',
  LEASE: 'Dlhodobý prenájom',
  AUCTION: 'Dražba',
  PURCHASE: 'Kúpa',
  EXCHANGE: 'Výmena',
}

// Ponuka pre property formuláre a filtre (admin aj mobil) — jediný zdroj
// pravdy, lokálne kópie driftovali. PURCHASE tu nie je: to je dopytová
// strana (portal user hľadá kúpu), nehnuteľnosť sa s ňou nevytvára.
export const PROPERTY_TRANSACTION_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  'SALE', 'RENT', 'LEASE', 'AUCTION', 'EXCHANGE',
].map((value) => ({ value, label: PROPERTY_TRANSACTION_TYPE_LABELS[value] ?? value }))

// Single-sourced z UC tabuliek (uc/attributes.ts) — žiadne ručné duplikáty,
// hodnoty sú viazané na Prisma enum contract testom (property-enums.contract.test.ts).
// OPTIONS typ sa necháva odvodiť (ucOptions je generický cez keyof tabuľky) —
// explicitná `Array<{value,label}>` anotácia by to zúženie zahodila.
export const PROPERTY_CONDITION_LABELS: Record<string, string> = ucLabels(UC_CONDITIONS)
export const PROPERTY_CONDITION_OPTIONS = ucOptions(UC_CONDITIONS)

export const PROPERTY_OWNERSHIP_LABELS: Record<string, string> = ucLabels(UC_OWNERSHIPS)
export const PROPERTY_OWNERSHIP_OPTIONS = ucOptions(UC_OWNERSHIPS)

// Nové UC-viazané atribúty (uc_taxonomy migrácia) — single-sourced z uc/attributes.ts.
// Jednohodnotové (majú Prisma enum + contract test row): equip type, position,
// territory, ground type, house type, ready-to-build.
export const PROPERTY_EQUIP_TYPE_LABELS: Record<string, string> = ucLabels(UC_EQUIP_TYPES)
export const PROPERTY_EQUIP_TYPE_OPTIONS = ucOptions(UC_EQUIP_TYPES)

export const PROPERTY_POSITION_LABELS: Record<string, string> = ucLabels(UC_POSITIONS)
export const PROPERTY_POSITION_OPTIONS = ucOptions(UC_POSITIONS)

export const PROPERTY_TERRITORY_LABELS: Record<string, string> = ucLabels(UC_TERRITORIES)
export const PROPERTY_TERRITORY_OPTIONS = ucOptions(UC_TERRITORIES)

export const PROPERTY_GROUND_TYPE_LABELS: Record<string, string> = ucLabels(UC_GROUND_TYPES)
export const PROPERTY_GROUND_TYPE_OPTIONS = ucOptions(UC_GROUND_TYPES)

export const PROPERTY_HOUSE_TYPE_LABELS: Record<string, string> = ucLabels(UC_HOUSE_TYPES)
export const PROPERTY_HOUSE_TYPE_OPTIONS = ucOptions(UC_HOUSE_TYPES)

export const PROPERTY_READY_TO_BUILD_LABELS: Record<string, string> = ucLabels(UC_READY_TO_BUILD)
export const PROPERTY_READY_TO_BUILD_OPTIONS = ucOptions(UC_READY_TO_BUILD)

// Viacnásobné výbery (Property.* stĺpce sú String[], BEZ Prisma enumu a teda
// bez riadku v property-enums.contract.test.ts) — heatingSystems, orientation,
// buildingType, typeOfParking a 4 utilities nižšie. Lookup z runtime DB stringu
// preto vždy s `?? value` fallbackom (neznáma/legacy hodnota sa nesmie stratiť).
export const PROPERTY_HEATING_SYSTEM_LABELS: Record<string, string> = ucLabels(UC_HEATING_SYSTEMS)
export const PROPERTY_HEATING_SYSTEM_OPTIONS = ucOptions(UC_HEATING_SYSTEMS)

export const PROPERTY_ORIENTATION_LABELS: Record<string, string> = ucLabels(UC_ORIENTATIONS)
export const PROPERTY_ORIENTATION_OPTIONS = ucOptions(UC_ORIENTATIONS)

export const PROPERTY_BUILDING_TYPE_LABELS: Record<string, string> = ucLabels(UC_BUILDING_TYPES)
export const PROPERTY_BUILDING_TYPE_OPTIONS = ucOptions(UC_BUILDING_TYPES)

export const PROPERTY_PARKING_TYPE_LABELS: Record<string, string> = ucLabels(UC_PARKING_TYPES)
export const PROPERTY_PARKING_TYPE_OPTIONS = ucOptions(UC_PARKING_TYPES)

export const PROPERTY_UTILITIES_WATER_LABELS: Record<string, string> = ucLabels(UC_WATER)
export const PROPERTY_UTILITIES_WATER_OPTIONS = ucOptions(UC_WATER)

export const PROPERTY_UTILITIES_ELECTRIC_LABELS: Record<string, string> = ucLabels(UC_ELECTRIC)
export const PROPERTY_UTILITIES_ELECTRIC_OPTIONS = ucOptions(UC_ELECTRIC)

export const PROPERTY_UTILITIES_SEWAGE_LABELS: Record<string, string> = ucLabels(UC_SEWAGE)
export const PROPERTY_UTILITIES_SEWAGE_OPTIONS = ucOptions(UC_SEWAGE)

export const PROPERTY_UTILITIES_GAS_LABELS: Record<string, string> = ucLabels(UC_GAS)
export const PROPERTY_UTILITIES_GAS_OPTIONS = ucOptions(UC_GAS)

/**
 * @deprecated Formulárové pole `heating` (single-select palivo) nahradil
 * viacnásobný výber `heatingSystems` s labelmi v `PROPERTY_HEATING_SYSTEM_LABELS`
 * (`UC_HEATING_SYSTEMS`) — vlna 3 UC exportu ho odstráni z admin/mobile
 * formulárov; `PropertyDto.heating` je preto už odstránené. Táto mapa ostáva
 * NATRVALO (nie dočasne): `property_revisions` obsahujú historické `heating`
 * diffy a `PropertyHistoryPanel` ich cez ňu (a `PROPERTY_FIELD_LABELS.heating`)
 * renderuje.
 */
export const PROPERTY_HEATING_LABELS: Record<string, string> = {
  GAS: 'Plyn',
  ELECTRIC: 'Elektrina',
  SOLID_FUEL: 'Tuhé palivo',
  DISTRICT: 'Centrálne',
  HEAT_PUMP: 'Tepelné čerpadlo',
  SOLAR: 'Solárne',
  OTHER: 'Iné',
}

// Property.priceType — plné labely (select v PropertyPriceSection, história zmien).
// PRICE_TYPE_SUFFIX vyššie je len suffix za cenou.
export const PRICE_TYPE_LABELS: Record<string, string> = {
  TOTAL: 'Celková',
  PER_M2: '€/m²',
  PER_MONTH: '€/mesiac',
  PER_YEAR: '€/rok',
  PER_M2_MONTH: '€/m²/mesiac',
  PER_M2_YEAR: '€/m²/rok',
  PER_DAY: '€/deň',
  ON_REQUEST: 'Cena na vyžiadanie',
  BY_AGREEMENT: 'Cena dohodou',
}
export const PRICE_TYPE_OPTIONS: Array<{ value: string; label: string }> = Object.entries(
  PRICE_TYPE_LABELS,
).map(([value, label]) => ({ value, label }))

// Energetická trieda (A0–G) — select vo formulári inzerátu; label = hodnota.
export const ENERGY_CLASS_OPTIONS: Array<{ value: PropertyEnergyClass; label: string }> = (
  Object.keys(ENERGY_CLASS_TO_UC_CERT) as PropertyEnergyClass[]
).map((value) => ({ value, label: value }))

// Slovenské labely polí modelu Property (kľúč = camelCase názov stĺpca).
// Jediný zdroj pravdy — používa PropertySaveError (server-side validačné chyby)
// aj história zmien (PropertyHistoryPanel). Pole bez záznamu tu sa zobrazí
// surovým kľúčom — pri pridaní stĺpca do schémy doplň label.
export const PROPERTY_FIELD_LABELS: Record<string, string> = {
  title: 'Názov',
  titleSk: 'Názov (SK)',
  titleEn: 'Názov (EN)',
  propertyType: 'Typ nehnuteľnosti',
  propertySubtype: 'Podtyp',
  subtypeNeedsReview: 'Podtyp na dotriedenie',
  transactionType: 'Typ transakcie',
  status: 'Stav inzerátu',
  agentId: 'Maklér',
  branchId: 'Pobočka',
  price: 'Cena',
  priceVat: 'Cena s DPH',
  priceType: 'Typ ceny',
  priceNegotiable: 'Cena dohodou možná',
  priceHidden: 'Skrytá cena',
  rentPrice: 'Cena prenájmu',
  monthlyCharges: 'Mesačné náklady',
  commissionAmount: 'Provízia',
  currency: 'Mena',
  street: 'Ulica',
  streetNumber: 'Súpisné číslo',
  city: 'Mesto alebo obec',
  cityPart: 'Mestská časť',
  district: 'Okres',
  region: 'Kraj',
  cadastralArea: 'Katastrálne územie',
  country: 'Krajina',
  zip: 'PSČ',
  lat: 'Zemepisná šírka',
  lng: 'Zemepisná dĺžka',
  locationHidden: 'Skrytá adresa',
  ucRegionId: 'Obec (portál)',
  ucCitypartId: 'Mestská časť (portál)',
  ucStreetId: 'Ulica (portál)',
  floorArea: 'Podlahová plocha',
  usableArea: 'Úžitková plocha',
  landArea: 'Plocha pozemku',
  builtupArea: 'Zastavaná plocha',
  roomsCount: 'Izby',
  bedroomsCount: 'Spálne',
  bathroomsCount: 'Kúpeľne',
  floor: 'Podlažie',
  totalFloors: 'Celkom podlaží',
  condition: 'Stav',
  ownership: 'Vlastníctvo',
  constructionYear: 'Rok výstavby',
  reconstructionYear: 'Rok rekonštrukcie',
  energyClass: 'Energetická trieda',
  hasEnergyCertificate: 'Energetický certifikát',
  // história — needeletovať (properties.heating už neexistuje, ale
  // property_revisions staré diffy majú a PropertyHistoryPanel ich renderuje)
  heating: 'Vykurovanie',
  annualHeatCost: 'Ročné náklady na kúrenie',
  equipType: 'Vybavenie (zariadenosť)',
  heatingSystems: 'Vykurovacie systémy',
  position: 'Umiestnenie podlažia',
  territory: 'Územie',
  typeOfHouse: 'Typ domu',
  groundType: 'Typ pozemku (svahovitosť)',
  readyToBuild: 'Pripravenosť na výstavbu',
  orientation: 'Orientácia',
  buildingType: 'Konštrukcia',
  typeOfParking: 'Typ parkovania',
  utilitiesWater: 'Voda',
  utilitiesElectric: 'Elektrina',
  utilitiesSewage: 'Kanalizácia',
  utilitiesGas: 'Plyn',
  guarantee: 'Zábezpeka',
  powerCosts: 'Náklady na energie',
  powerCostsInPrice: 'Energie v cene',
  description: 'Popis',
  descriptionSk: 'Popis (SK)',
  descriptionEn: 'Popis (EN)',
  videoUrl: 'Video (URL)',
  coverOverlay: 'Cover titulnej fotky',
  hasBalcony: 'Balkón',
  hasTerrace: 'Terasa',
  hasLoggia: 'Lodžia',
  hasCellar: 'Pivnica',
  hasStorageRoom: 'Kobka',
  hasParking: 'Parkovanie',
  hasGarage: 'Garáž',
  hasElevator: 'Výťah',
  hasPool: 'Bazén',
  hasSauna: 'Sauna',
  hasSecurity: 'Zabezpečenie',
  hasSmartHome: 'Smart home',
  isBarrierFree: 'Bezbariérový',
  hasGarden: 'Záhrada',
  // história — needeletovať (properties.hasFurnished už neexistuje, nahradil
  // ho equipType, ale property_revisions staré diffy majú)
  hasFurnished: 'Zariadený',
  dealType: 'Typ ponuky',
  // história — needeletovať (staré property_revisions diffy)
  isExclusive: 'Exkluzívna ponuka',
  isAuction: 'Dražba',
  mortgagePossible: 'Možnosť hypotéky',
  availableFrom: 'Voľné od',
  seoTitle: 'SEO titulok',
  seoDescription: 'SEO popis',
}

// Rodinný stav strany (Client.maritalStatus) — formulár zákazníka a formulár strany.
export const MARITAL_STATUS_LABELS: Record<string, string> = {
  UNKNOWN: 'Neznámy',
  SINGLE: 'Slobodný/á',
  MARRIED: 'Ženatý/vydatá',
} satisfies Record<MaritalStatus, string>

export const CALL_DIRECTION_LABELS: Record<string, string> = {
  INCOMING: 'Prichádzajúci',
  OUTGOING: 'Odchádzajúci',
  MISSED: 'Zmeškaný',
}

// ───────────────────────────────────────────────────────────────────────────
// Badge color maps — single source of truth for status → Tailwind `.badge-*`
// color, mirroring CUSTOMER_TYPE_BADGE_CLASS above. Pages previously redeclared
// these per-file with diverging colors; centralized here for consistency.
// Semantic convention: blue = early/info, yellow = pending/in-progress,
// green = success/done, red = lost/cancelled, gray = neutral/inactive.
// ───────────────────────────────────────────────────────────────────────────

// PropertyStatus
export const PROPERTY_STATUS_BADGE_CLASS: Record<string, string> = {
  DRAFT: 'badge-gray',
  IN_REVIEW: 'badge-yellow',
  ACTIVE: 'badge-green',
  RESERVED: 'badge-yellow',
  SOLD: 'badge-blue',
  RENTED: 'badge-blue',
  INACTIVE: 'badge-gray',
  ARCHIVED: 'badge-gray',
}

// UserStatus
export const USER_STATUS_BADGE_CLASS: Record<string, string> = {
  ACTIVE: 'badge-green',
  INACTIVE: 'badge-gray',
  INVITED: 'badge-yellow',
  SUSPENDED: 'badge-red',
}

// LeadStatus
export const LEAD_STATUS_BADGE_CLASS: Record<string, string> = {
  NEW: 'badge-blue',
  CONTACTED: 'badge-yellow',
}

// EventStatus / ViewingStatus (shared enum)
export const EVENT_STATUS_BADGE_CLASS: Record<string, string> = {
  SCHEDULED: 'badge-blue',
  CONFIRMED: 'badge-yellow',
  DONE: 'badge-green',
  CANCELLED: 'badge-red',
}

// TaskStatus
export const TASK_STATUS_BADGE_CLASS: Record<string, string> = {
  TODO: 'badge-blue',
  IN_PROGRESS: 'badge-yellow',
  DONE: 'badge-green',
  CANCELLED: 'badge-gray',
}

// TaskPriority
export const TASK_PRIORITY_BADGE_CLASS: Record<string, string> = {
  LOW: 'badge-gray',
  MEDIUM: 'badge-blue',
  HIGH: 'badge-yellow',
  URGENT: 'badge-red',
}

// Farebná bodka priority pri úlohe (Tailwind triedy pozadia)
export const TASK_PRIORITY_DOT_CLASS: Record<string, string> = {
  LOW: 'bg-gray-300 dark:bg-gray-600',
  MEDIUM: 'bg-amber-400',
  HIGH: 'bg-orange-500',
  URGENT: 'bg-red-500',
}

// ActivityAction — audit log akcie
export const ACTIVITY_ACTION_LABELS: Record<string, string> = {
  CREATED: 'Vytvorené',
  UPDATED: 'Upravené',
  DELETED: 'Zmazané',
  PUBLISHED: 'Publikované',
  ARCHIVED: 'Archivované',
  STATUS_CHANGED: 'Zmena stavu',
  ASSIGNED: 'Priradené',
  COMMENTED: 'Komentár',
  EXPORTED: 'Exportované',
  VIEWED: 'Zobrazené',
  CALLED: 'Hovor',
  EMAILED: 'E-mail',
  MEETING: 'Stretnutie',
  DOCUMENT_SIGNED: 'Dokument podpísaný',
}

export const ACTIVITY_ACTION_BADGE_CLASS: Record<string, string> = {
  CREATED: 'badge-green',
  UPDATED: 'badge-yellow',
  DELETED: 'badge-red',
  PUBLISHED: 'badge-blue',
  ARCHIVED: 'badge-gray',
  STATUS_CHANGED: 'badge-blue',
  ASSIGNED: 'badge-blue',
  COMMENTED: 'badge-gray',
  EXPORTED: 'badge-blue',
  VIEWED: 'badge-gray',
  CALLED: 'badge-blue',
  EMAILED: 'badge-blue',
  MEETING: 'badge-blue',
  DOCUMENT_SIGNED: 'badge-green',
}

// ActivityLog entityType → slovenský názov entity
export const ACTIVITY_ENTITY_LABELS: Record<string, string> = {
  contact: 'Kontakt',
  // Staré hodnoty — migrácia 20261101100000 ich v DB prepísala na contact; admin ich ešte používa.
  client: 'Zákazník',
  lead: 'Kontakt',
  deal: 'Obchod',
  property: 'Nehnuteľnosť',
  'lawyer-request': 'Právna požiadavka',
  task: 'Úloha',
  viewing: 'Obhliadka',
  commission: 'Provízia',
  call_log: 'Hovor',
  import_job: 'Import',
  note: 'Poznámka',
}

/** Dôvod neaktívneho záujmu (blok „Neaktívne" v detaile kontaktu/zákazníka). */
export const INTEREST_INACTIVE_REASON_LABELS: Record<'MANUAL' | 'SUPERSEDED', string> = {
  MANUAL: 'Označený ako neaktívny',
  SUPERSEDED: 'Prevzal iný maklér',
}
